import { adminApi as api } from "@/store/apis/adminApi";

export const addTagTypes = [
  "Auth",
  "Staff",
  "Dashboard",
  "Advice",
  "Popups",
  "Uploads",
  "Circle",
  "Audit",
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      adminSignIn: build.mutation<AdminSignInApiResponse, AdminSignInApiArg>({
        query: (queryArg) => ({ url: `/admin/auth/sign-in`, method: "POST", body: queryArg.body }),
        invalidatesTags: ["Auth"],
      }),
      adminMfaEnroll: build.mutation<AdminMfaEnrollApiResponse, AdminMfaEnrollApiArg>({
        query: () => ({ url: `/admin/auth/mfa/enroll`, method: "POST" }),
        invalidatesTags: ["Auth"],
      }),
      adminMfaVerify: build.mutation<AdminMfaVerifyApiResponse, AdminMfaVerifyApiArg>({
        query: (queryArg) => ({
          url: `/admin/auth/mfa/verify`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Auth"],
      }),
      adminRefresh: build.mutation<AdminRefreshApiResponse, AdminRefreshApiArg>({
        query: (queryArg) => ({ url: `/admin/auth/refresh`, method: "POST", body: queryArg.body }),
        invalidatesTags: ["Auth"],
      }),
      adminSignOut: build.mutation<AdminSignOutApiResponse, AdminSignOutApiArg>({
        query: () => ({ url: `/admin/auth/sign-out`, method: "POST" }),
        invalidatesTags: ["Auth"],
      }),
      getMe: build.query<GetMeApiResponse, GetMeApiArg>({
        query: () => ({ url: `/admin/me` }),
        providesTags: ["Staff"],
      }),
      listRoles: build.query<ListRolesApiResponse, ListRolesApiArg>({
        query: () => ({ url: `/admin/roles` }),
        providesTags: ["Staff"],
      }),
      listAdminUsers: build.query<ListAdminUsersApiResponse, ListAdminUsersApiArg>({
        query: (queryArg) => ({
          url: `/admin/users`,
          params: {
            limit: queryArg.limit,
            offset: queryArg.offset,
            q: queryArg.q,
            role: queryArg.role,
            active: queryArg.active,
            account_kind: queryArg.accountKind,
            sort: queryArg.sort,
          },
        }),
        providesTags: ["Staff"],
      }),
      createAdminUser: build.mutation<CreateAdminUserApiResponse, CreateAdminUserApiArg>({
        query: (queryArg) => ({ url: `/admin/users`, method: "POST", body: queryArg.body }),
        invalidatesTags: ["Staff"],
      }),
      getAdminUser: build.query<GetAdminUserApiResponse, GetAdminUserApiArg>({
        query: (queryArg) => ({ url: `/admin/users/${queryArg.id}` }),
        providesTags: ["Staff"],
      }),
      updateAdminUser: build.mutation<UpdateAdminUserApiResponse, UpdateAdminUserApiArg>({
        query: (queryArg) => ({
          url: `/admin/users/${queryArg.id}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["Staff"],
      }),
      deactivateAdminUser: build.mutation<
        DeactivateAdminUserApiResponse,
        DeactivateAdminUserApiArg
      >({
        query: (queryArg) => ({ url: `/admin/users/${queryArg.id}`, method: "DELETE" }),
        invalidatesTags: ["Staff"],
      }),
      resetAdminUserMfa: build.mutation<ResetAdminUserMfaApiResponse, ResetAdminUserMfaApiArg>({
        query: (queryArg) => ({ url: `/admin/users/${queryArg.id}/mfa`, method: "DELETE" }),
        invalidatesTags: ["Staff"],
      }),
      getSummary: build.query<GetSummaryApiResponse, GetSummaryApiArg>({
        query: () => ({ url: `/admin/summary` }),
        providesTags: ["Dashboard"],
      }),
      listAdvice: build.query<ListAdviceApiResponse, ListAdviceApiArg>({
        query: (queryArg) => ({
          url: `/admin/advice`,
          params: {
            limit: queryArg.limit,
            offset: queryArg.offset,
            q: queryArg.q,
            category: queryArg.category,
            active: queryArg.active,
            sort: queryArg.sort,
          },
        }),
        providesTags: ["Advice"],
      }),
      createAdvice: build.mutation<CreateAdviceApiResponse, CreateAdviceApiArg>({
        query: (queryArg) => ({ url: `/admin/advice`, method: "POST", body: queryArg.body }),
        invalidatesTags: ["Advice"],
      }),
      getAdvice: build.query<GetAdviceApiResponse, GetAdviceApiArg>({
        query: (queryArg) => ({ url: `/admin/advice/${queryArg.id}` }),
        providesTags: ["Advice"],
      }),
      updateAdvice: build.mutation<UpdateAdviceApiResponse, UpdateAdviceApiArg>({
        query: (queryArg) => ({
          url: `/admin/advice/${queryArg.id}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["Advice"],
      }),
      deleteAdvice: build.mutation<DeleteAdviceApiResponse, DeleteAdviceApiArg>({
        query: (queryArg) => ({ url: `/admin/advice/${queryArg.id}`, method: "DELETE" }),
        invalidatesTags: ["Advice"],
      }),
      listAdviceCategories: build.query<
        ListAdviceCategoriesApiResponse,
        ListAdviceCategoriesApiArg
      >({
        query: (queryArg) => ({
          url: `/admin/advice-categories`,
          params: {
            limit: queryArg.limit,
            offset: queryArg.offset,
            q: queryArg.q,
            sort: queryArg.sort,
          },
        }),
        providesTags: ["Advice"],
      }),
      createAdviceCategory: build.mutation<
        CreateAdviceCategoryApiResponse,
        CreateAdviceCategoryApiArg
      >({
        query: (queryArg) => ({
          url: `/admin/advice-categories`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["Advice"],
      }),
      getAdviceCategory: build.query<GetAdviceCategoryApiResponse, GetAdviceCategoryApiArg>({
        query: (queryArg) => ({ url: `/admin/advice-categories/${queryArg.slug}` }),
        providesTags: ["Advice"],
      }),
      updateAdviceCategory: build.mutation<
        UpdateAdviceCategoryApiResponse,
        UpdateAdviceCategoryApiArg
      >({
        query: (queryArg) => ({
          url: `/admin/advice-categories/${queryArg.slug}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["Advice"],
      }),
      deleteAdviceCategory: build.mutation<
        DeleteAdviceCategoryApiResponse,
        DeleteAdviceCategoryApiArg
      >({
        query: (queryArg) => ({
          url: `/admin/advice-categories/${queryArg.slug}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Advice"],
      }),
      listPopups: build.query<ListPopupsApiResponse, ListPopupsApiArg>({
        query: (queryArg) => ({
          url: `/admin/popups`,
          params: {
            limit: queryArg.limit,
            offset: queryArg.offset,
            q: queryArg.q,
            status: queryArg.status,
            platform: queryArg.platform,
            stage: queryArg.stage,
            active: queryArg.active,
            sort: queryArg.sort,
          },
        }),
        providesTags: ["Popups"],
      }),
      createPopup: build.mutation<CreatePopupApiResponse, CreatePopupApiArg>({
        query: (queryArg) => ({ url: `/admin/popups`, method: "POST", body: queryArg.body }),
        invalidatesTags: ["Popups"],
      }),
      getPopup: build.query<GetPopupApiResponse, GetPopupApiArg>({
        query: (queryArg) => ({ url: `/admin/popups/${queryArg.id}` }),
        providesTags: ["Popups"],
      }),
      updatePopup: build.mutation<UpdatePopupApiResponse, UpdatePopupApiArg>({
        query: (queryArg) => ({
          url: `/admin/popups/${queryArg.id}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["Popups"],
      }),
      archivePopup: build.mutation<ArchivePopupApiResponse, ArchivePopupApiArg>({
        query: (queryArg) => ({ url: `/admin/popups/${queryArg.id}`, method: "DELETE" }),
        invalidatesTags: ["Popups"],
      }),
      createUpload: build.mutation<CreateUploadApiResponse, CreateUploadApiArg>({
        query: (queryArg) => ({ url: `/admin/uploads`, method: "POST", body: queryArg.body }),
        invalidatesTags: ["Uploads"],
      }),
      listCircleGroups: build.query<ListCircleGroupsApiResponse, ListCircleGroupsApiArg>({
        query: (queryArg) => ({
          url: `/admin/circle/groups`,
          params: {
            limit: queryArg.limit,
            offset: queryArg.offset,
            q: queryArg.q,
            stage: queryArg.stage,
            status: queryArg.status,
            sort: queryArg.sort,
          },
        }),
        providesTags: ["Circle"],
      }),
      getCircleGroup: build.query<GetCircleGroupApiResponse, GetCircleGroupApiArg>({
        query: (queryArg) => ({ url: `/admin/circle/groups/${queryArg.id}` }),
        providesTags: ["Circle"],
      }),
      listAuditLogs: build.query<ListAuditLogsApiResponse, ListAuditLogsApiArg>({
        query: (queryArg) => ({
          url: `/admin/audit-logs`,
          params: {
            limit: queryArg.limit,
            cursor: queryArg.cursor,
            actor_id: queryArg.actorId,
            resource_type: queryArg.resourceType,
            resource_id: queryArg.resourceId,
            action: queryArg.action,
            from: queryArg["from"],
            to: queryArg.to,
          },
        }),
        providesTags: ["Audit"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as adminApi };
export type AdminSignInApiResponse = /** status 200 Sesión del backoffice. */ AdminSession;
export type AdminSignInApiArg = {
  body: {
    email: string;
    password: string;
  };
};
export type AdminMfaEnrollApiResponse =
  /** status 201 Factor creado (pendiente de verificar). */ TotpEnrollment;
export type AdminMfaEnrollApiArg = void;
export type AdminMfaVerifyApiResponse = /** status 200 Sesión del backoffice. */ AdminSession;
export type AdminMfaVerifyApiArg = {
  body: {
    factor_id: string;
    code: string;
  };
};
export type AdminRefreshApiResponse = /** status 200 Sesión del backoffice. */ AdminSession;
export type AdminRefreshApiArg = {
  body: {
    refresh_token: string;
  };
};
export type AdminSignOutApiResponse = unknown;
export type AdminSignOutApiArg = void;
export type GetMeApiResponse = /** status 200 OK */ Admin;
export type GetMeApiArg = void;
export type ListRolesApiResponse = /** status 200 OK */ {
  items: Role[];
};
export type ListRolesApiArg = void;
export type ListAdminUsersApiResponse = /** status 200 OK */ AdminUserPage;
export type ListAdminUsersApiArg = {
  limit?: number;
  offset?: number;
  /** Búsqueda "contiene" (máx. 100). */
  q?: string;
  role?: string;
  active?: boolean;
  accountKind?: AccountKind;
  sort?:
    | "full_name"
    | "-full_name"
    | "email"
    | "-email"
    | "created_at"
    | "-created_at"
    | "last_login_at"
    | "-last_login_at";
};
export type CreateAdminUserApiResponse =
  /** status 201 Creado o vinculado. Deberá enrolar MFA en su primer login al backoffice. */ AdminUser;
export type CreateAdminUserApiArg = {
  body: {
    email: string;
    full_name: string;
    role: string;
    /** Obligatoria para cuenta nueva; prohibida al vincular. */
    password?: string;
    link_existing_account?: boolean;
  };
};
export type GetAdminUserApiResponse = /** status 200 OK */ AdminUser;
export type GetAdminUserApiArg = {
  id: string;
};
export type UpdateAdminUserApiResponse = /** status 200 OK */ AdminUser;
export type UpdateAdminUserApiArg = {
  id: string;
  body: {
    full_name?: string;
    role?: string;
    active?: boolean;
    password?: string;
  };
};
export type DeactivateAdminUserApiResponse = unknown;
export type DeactivateAdminUserApiArg = {
  id: string;
};
export type ResetAdminUserMfaApiResponse = unknown;
export type ResetAdminUserMfaApiArg = {
  id: string;
};
export type GetSummaryApiResponse = /** status 200 OK */ Summary;
export type GetSummaryApiArg = void;
export type ListAdviceApiResponse = /** status 200 OK */ AdvicePage;
export type ListAdviceApiArg = {
  limit?: number;
  offset?: number;
  /** Búsqueda "contiene" (máx. 100). */
  q?: string;
  category?: string;
  active?: boolean;
  sort?: "position" | "-position" | "updated_at" | "-updated_at" | "created_at" | "-created_at";
};
export type CreateAdviceApiResponse = /** status 201 Creado */ Advice;
export type CreateAdviceApiArg = {
  body: {
    category: string;
    body: string;
    position?: number;
    active?: boolean;
  };
};
export type GetAdviceApiResponse = /** status 200 OK */ Advice;
export type GetAdviceApiArg = {
  id: number;
};
export type UpdateAdviceApiResponse = /** status 200 OK */ Advice;
export type UpdateAdviceApiArg = {
  id: number;
  body: {
    category?: string;
    body?: string;
    position?: number;
    active?: boolean;
  };
};
export type DeleteAdviceApiResponse = unknown;
export type DeleteAdviceApiArg = {
  id: number;
};
export type ListAdviceCategoriesApiResponse = /** status 200 OK */ AdviceCategoryPage;
export type ListAdviceCategoriesApiArg = {
  limit?: number;
  offset?: number;
  /** Búsqueda "contiene" (máx. 100). */
  q?: string;
  sort?: "position" | "-position" | "label" | "-label";
};
export type CreateAdviceCategoryApiResponse = /** status 201 Creada */ AdviceCategory;
export type CreateAdviceCategoryApiArg = {
  body: {
    slug: string;
    label: string;
    emoji?: string | null;
    position?: number;
  };
};
export type GetAdviceCategoryApiResponse = /** status 200 OK */ AdviceCategory;
export type GetAdviceCategoryApiArg = {
  slug: string;
};
export type UpdateAdviceCategoryApiResponse = /** status 200 OK */ AdviceCategory;
export type UpdateAdviceCategoryApiArg = {
  slug: string;
  body: {
    label?: string;
    emoji?: string | null;
    position?: number;
  };
};
export type DeleteAdviceCategoryApiResponse = unknown;
export type DeleteAdviceCategoryApiArg = {
  slug: string;
};
export type ListPopupsApiResponse = /** status 200 OK */ PopupPage;
export type ListPopupsApiArg = {
  limit?: number;
  offset?: number;
  /** Búsqueda "contiene" (máx. 100). */
  q?: string;
  status?: PopupStatus;
  platform?: "ios" | "android";
  /** Incluye los pop-ups dirigidos a todas las etapas (`target_stages` vacío). */
  stage?: Stage;
  active?: boolean;
  sort?:
    | "created_at"
    | "-created_at"
    | "updated_at"
    | "-updated_at"
    | "start_at"
    | "-start_at"
    | "priority"
    | "-priority";
};
export type CreatePopupApiResponse = /** status 201 Creado */ Popup;
export type CreatePopupApiArg = {
  body: PopupInput & object;
};
export type GetPopupApiResponse = /** status 200 OK */ Popup;
export type GetPopupApiArg = {
  id: string;
};
export type UpdatePopupApiResponse = /** status 200 OK */ Popup;
export type UpdatePopupApiArg = {
  id: string;
  body: PopupInput & object;
};
export type ArchivePopupApiResponse = unknown;
export type ArchivePopupApiArg = {
  id: string;
};
export type CreateUploadApiResponse = /** status 201 URL emitida */ {
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  public_url: string;
};
export type CreateUploadApiArg = {
  body: {
    purpose: Purpose;
    content_type: Content_type;
  };
};
export type ListCircleGroupsApiResponse = /** status 200 OK */ CircleGroupPage;
export type ListCircleGroupsApiArg = {
  limit?: number;
  offset?: number;
  /** Búsqueda por título. */
  q?: string;
  stage?: Stage;
  status?: "active" | "archived";
  /** Siempre descendente. */
  sort?: "last_activity_at" | "member_count" | "created_at";
};
export type GetCircleGroupApiResponse = /** status 200 OK */ CircleGroupDetail;
export type GetCircleGroupApiArg = {
  id: string;
};
export type ListAuditLogsApiResponse = /** status 200 OK */ {
  items: AuditLog[];
  next_cursor: number | null;
};
export type ListAuditLogsApiArg = {
  limit?: number;
  cursor?: number;
  actorId?: string;
  resourceType?: string;
  resourceId?: string;
  action?: string;
  from?: string;
  to?: string;
};
export type Admin = {
  id: string;
  email: string;
  full_name: string;
  role: string;
  active: boolean;
  created_at?: string;
  permissions: Permission[];
};
export type MfaState = {
  status: Status;
  /** Factor a verificar (solo en `challenge_required`). */
  factor_id?: string;
};
export type AdminSession = {
  access_token: string;
  refresh_token: string;
  token_type?: string;
  expires_in: number;
  expires_at: string;
  user?: {
    id?: string;
    email?: string;
  };
  admin: Admin;
  mfa: MfaState;
};
export type ErrorResponse = {
  status_code: number;
  code: Code;
  message: string;
  field?: string;
  details?: {
    [key: string]: any;
  };
};
export type TotpEnrollment = {
  factor_id: string;
  /** SVG como data URI (usar en <img src>). */
  qr_code: string;
  /** Para ingresar a mano. */
  secret: string;
  uri: string;
};
export type Role = {
  id: string;
  label: string;
  description?: string | null;
  permissions: Permission[];
};
export type PageMeta = {
  total: number;
  limit: number;
  offset: number;
};
export type Audit = {
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
};
export type AdminUser = {
  id: string;
  email: string;
  full_name: string;
  role: string;
  active: boolean;
  last_login_at?: string | null;
  account_kind?: AccountKind;
} & Audit;
export type AdminUserPage = PageMeta & {
  items: AdminUser[];
};
export type Summary = {
  advice: {
    total?: number;
    active?: number;
    categories?: number;
  };
  popups: {
    live?: number;
    scheduled?: number;
    paused?: number;
    expired?: number;
  };
  circle: {
    groups_active?: number;
    groups_archived?: number;
  };
  generated_at: string;
};
export type Advice = {
  id: number;
  /** Slug de `AdviceCategory`. */
  category: string;
  position: number;
  body: string;
  active: boolean;
} & Audit;
export type AdvicePage = PageMeta & {
  items: Advice[];
};
export type AdviceCategory = {
  slug: string;
  label: string;
  emoji?: string | null;
  position: number;
} & Audit;
export type AdviceCategoryPage = PageMeta & {
  items: AdviceCategory[];
};
export type PopupInput = {
  title?: string;
  body?: string;
  /** https. */
  image_url?: string | null;
  /** Va junto con cta_url. */
  cta_text?: string | null;
  /** `arya://…` o `https://…`. Va junto con cta_text. */
  cta_url?: string | null;
  /** Vacío = todas las etapas. */
  target_stages?: Stage[];
  platforms?: Platforms[];
  frequency?: Frequency;
  priority?: number;
  start_at?: string | null;
  /** Posterior a start_at. */
  end_at?: string | null;
  active?: boolean;
};
export type Popup = PopupInput & {
  id: string;
  archived_at?: string | null;
  status: PopupStatus;
} & Audit;
export type PopupPage = PageMeta & {
  items: Popup[];
};
export type CircleGroup = {
  id: string;
  title: string;
  avatar_bg?: string | null;
  stages: Stage[];
  member_count: number;
  status: Status2;
  last_activity_at: string;
  archived_at?: string | null;
  created_at: string;
};
export type CircleGroupPage = PageMeta & {
  items: CircleGroup[];
};
export type CircleGroupDetail = CircleGroup & {
  messages_count_7d: number;
  /** Reportes de mensajes del grupo (no incluye DMs). */
  reports_count: number;
  /** pending + reviewing. */
  pending_reports_count: number;
};
export type AuditLog = {
  id: number;
  actor_id?: string | null;
  actor_email?: string | null;
  /** Rol de staff, `service_role` o `system` (dashboard / SQL / GoTrue). */
  actor_role?: string | null;
  /** `<tabla>.<create|update|delete>` o evento: `admin.sign_in`,
    `admin.mfa_enrolled`, `admin_users.password_reset`,
    `admin_users.password_change`, `admin_users.sessions_revoked`,
    `admin_users.mfa_reset`.
     */
  action: string;
  resource_type?: string | null;
  resource_id?: string | null;
  before_state?: {
    [key: string]: any;
  } | null;
  after_state?: {
    [key: string]: any;
  } | null;
  changed_fields?: string[] | null;
  ip_address?: string | null;
  user_agent?: string | null;
  /** = header X-Trace-Id. */
  request_id?: string | null;
  created_at: string;
};
export enum Permission {
  DashboardRead = "dashboard.read",
  AuditRead = "audit.read",
  AdviceRead = "advice.read",
  AdviceWrite = "advice.write",
  PopupsRead = "popups.read",
  PopupsWrite = "popups.write",
  CircleRead = "circle.read",
  ContentUpload = "content.upload",
  AdminsManage = "admins.manage",
}
export enum Status {
  Verified = "verified",
  ChallengeRequired = "challenge_required",
  EnrollRequired = "enroll_required",
}
export enum Code {
  ErrValidation = "ERR_VALIDATION",
  ErrInvalidCredentials = "ERR_INVALID_CREDENTIALS",
  ErrUnauthorized = "ERR_UNAUTHORIZED",
  ErrInvalidSession = "ERR_INVALID_SESSION",
  ErrForbidden = "ERR_FORBIDDEN",
  ErrMfaRequired = "ERR_MFA_REQUIRED",
  ErrNotFound = "ERR_NOT_FOUND",
  ErrConflict = "ERR_CONFLICT",
  ErrRateLimited = "ERR_RATE_LIMITED",
  ErrInternal = "ERR_INTERNAL",
}
export enum AccountKind {
  Staff = "staff",
  Linked = "linked",
}
export enum Stage {
  Trying = "trying",
  Fertility = "fertility",
  Loss = "loss",
  Lost = "lost",
}
export enum Platforms {
  Ios = "ios",
  Android = "android",
}
export enum Frequency {
  Once = "once",
  Daily = "daily",
  EverySession = "every_session",
}
export enum PopupStatus {
  Live = "live",
  Scheduled = "scheduled",
  Paused = "paused",
  Expired = "expired",
  Archived = "archived",
}
export enum Purpose {
  Popup = "popup",
}
export enum Content_type {
  ImagePng = "image/png",
  ImageJpeg = "image/jpeg",
  ImageWebp = "image/webp",
}
export enum Status2 {
  Active = "active",
  Archived = "archived",
}
export const {
  useAdminSignInMutation,
  useAdminMfaEnrollMutation,
  useAdminMfaVerifyMutation,
  useAdminRefreshMutation,
  useAdminSignOutMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useListRolesQuery,
  useLazyListRolesQuery,
  useListAdminUsersQuery,
  useLazyListAdminUsersQuery,
  useCreateAdminUserMutation,
  useGetAdminUserQuery,
  useLazyGetAdminUserQuery,
  useUpdateAdminUserMutation,
  useDeactivateAdminUserMutation,
  useResetAdminUserMfaMutation,
  useGetSummaryQuery,
  useLazyGetSummaryQuery,
  useListAdviceQuery,
  useLazyListAdviceQuery,
  useCreateAdviceMutation,
  useGetAdviceQuery,
  useLazyGetAdviceQuery,
  useUpdateAdviceMutation,
  useDeleteAdviceMutation,
  useListAdviceCategoriesQuery,
  useLazyListAdviceCategoriesQuery,
  useCreateAdviceCategoryMutation,
  useGetAdviceCategoryQuery,
  useLazyGetAdviceCategoryQuery,
  useUpdateAdviceCategoryMutation,
  useDeleteAdviceCategoryMutation,
  useListPopupsQuery,
  useLazyListPopupsQuery,
  useCreatePopupMutation,
  useGetPopupQuery,
  useLazyGetPopupQuery,
  useUpdatePopupMutation,
  useArchivePopupMutation,
  useCreateUploadMutation,
  useListCircleGroupsQuery,
  useLazyListCircleGroupsQuery,
  useGetCircleGroupQuery,
  useLazyGetCircleGroupQuery,
  useListAuditLogsQuery,
  useLazyListAuditLogsQuery,
} = injectedRtkApi;
